import { formatCoachingTime } from '@efdigital/ll-addon-coaching/utils/coaching-sessions/format-coaching-time';
import type { CoachingConfig } from '@efdigital/ll-addon-data/types/coaching/coaching-config';
import { debounce } from '@efdigital/ll-addon-ui/modifiers/debounce';
import type { EditingContext } from '@efdigital/ll-admin-v2/components/edit-context.gts';
import {
  EditRecord,
  initialState,
} from '@efdigital/ll-admin-v2/components/edit-record.gts';
import HelpPopover from '@efdigital/ll-admin-v2/components/help-popover.gts';
import eq from '@efdigital/ll-admin-v2/helpers/eq.ts';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';

const ALLOWED_DURATION_OPTIONS = [
  30, 45, 60, 75, 90, 120, 135, 150, 180,
] as const;

const CHEMISTRY_LENGTH_OPTIONS = [15, 30, 45, 60] as const;

const HOURS_PER_DAY = 24;

function hoursToDays(hours: number): number {
  return hours / HOURS_PER_DAY;
}

function daysToHours(days: number): number {
  return Math.round(days * HOURS_PER_DAY);
}

interface EditCoachingConfigSignature {
  Args: {
    coachingConfig: CoachingConfig;
    context: EditingContext;
    /** When false, the form is read-only and the Edit control is hidden. */
    canEdit?: boolean;
  };
}

export class EditCoachingConfig extends Component<EditCoachingConfigSignature> {
  readonly allowedDurationOptions = ALLOWED_DURATION_OPTIONS;
  readonly chemistryLengthOptions = CHEMISTRY_LENGTH_OPTIONS;

  get coachingHours(): number {
    return Math.floor(this.args.coachingConfig.coachingTimeMinutes / 60);
  }

  get coachingMinutesPart(): number {
    return this.args.coachingConfig.coachingTimeMinutes % 60;
  }

  get sortedAllowedDurations(): number[] {
    return [...this.args.coachingConfig.allowedSessionDurations].sort(
      (a, b) => a - b
    );
  }

  get estimatedCoachingSessionCount(): number {
    const durationMinutes =
      this.args.coachingConfig.defaultSessionDurationMinutes;
    return Math.floor(
      this.args.coachingConfig.coachingTimeMinutes / durationMinutes
    );
  }

  get estimatedCoachingSessionRemainderMinutes(): number {
    const durationMinutes =
      this.args.coachingConfig.defaultSessionDurationMinutes;
    return this.args.coachingConfig.coachingTimeMinutes % durationMinutes;
  }

  get hasEstimatedCoachingSessionRemainder(): boolean {
    return (
      this.canEstimateCoachingSessionCount &&
      this.estimatedCoachingSessionRemainderMinutes > 0
    );
  }

  get canEstimateCoachingSessionCount(): boolean {
    return this.args.coachingConfig.defaultSessionDurationMinutes > 0;
  }

  get coachConfirmationDaysAfterBookingChemistry(): number {
    return hoursToDays(
      this.args.coachingConfig.coachConfirmationHoursAfterBookingChemistry
    );
  }

  get coachConfirmationDaysAfterBookingCoaching(): number {
    return hoursToDays(
      this.args.coachingConfig.coachConfirmationHoursAfterBookingCoaching
    );
  }

  setName = (event: Event) => {
    this.args.coachingConfig.name = (event.target as HTMLInputElement).value;
  };

  setCoachingHours = (event: Event) => {
    const hours = Math.max(
      0,
      parseInt((event.target as HTMLInputElement).value, 10) || 0
    );
    this.args.coachingConfig.coachingTimeMinutes =
      hours * 60 + this.coachingMinutesPart;
  };

  setCoachingMinutesPart = (event: Event) => {
    const minutes = Math.min(
      59,
      Math.max(0, parseInt((event.target as HTMLInputElement).value, 10) || 0)
    );
    this.args.coachingConfig.coachingTimeMinutes =
      this.coachingHours * 60 + minutes;
  };

  adjustCoachingTimeMinutes = (deltaMinutes: number) => {
    this.args.coachingConfig.coachingTimeMinutes = Math.max(
      15,
      this.args.coachingConfig.coachingTimeMinutes + deltaMinutes
    );
  };

  setAllowedDuration = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const value = parseInt(target.value, 10);
    if (target.checked) {
      this.args.coachingConfig.allowedSessionDurations = [
        ...this.args.coachingConfig.allowedSessionDurations,
        value,
      ].sort();
      if (!this.args.coachingConfig.defaultSessionDurationMinutes) {
        this.args.coachingConfig.defaultSessionDurationMinutes = value;
      }
    } else {
      this.args.coachingConfig.allowedSessionDurations =
        this.args.coachingConfig.allowedSessionDurations.filter(
          (mins) => mins !== value
        );
      if (this.args.coachingConfig.defaultSessionDurationMinutes === value) {
        this.args.coachingConfig.defaultSessionDurationMinutes =
          this.args.coachingConfig.allowedSessionDurations[0] || 0;
      }
    }
  };

  isAllowedDuration = (mins: number): boolean => {
    return this.args.coachingConfig.allowedSessionDurations.includes(mins);
  };

  setDefaultDuration = (event: Event) => {
    const value = parseInt((event.target as HTMLSelectElement).value, 10);
    if (Number.isFinite(value)) {
      this.args.coachingConfig.defaultSessionDurationMinutes = value;
    }
  };

  setChemistrySessionLength = (mins: number) => {
    this.args.coachingConfig.chemistrySessionLengthMins = mins;
  };

  isChemistrySessionLength = (mins: number): boolean => {
    return this.args.coachingConfig.chemistrySessionLengthMins === mins;
  };

  adjustChemistryCallLimit = (delta: number) => {
    this.args.coachingConfig.chemistryCallLimit = Math.max(
      0,
      this.args.coachingConfig.chemistryCallLimit + delta
    );
  };

  setChemistryCallLimit = (event: Event) => {
    this.args.coachingConfig.chemistryCallLimit = parseInt(
      (event.target as HTMLInputElement).value,
      10
    );
  };

  setCancellationWindowHours = (event: Event) => {
    this.args.coachingConfig.cancellationWindowHours = parseInt(
      (event.target as HTMLInputElement).value,
      10
    );
  };

  setSchedulingWindowDays = (event: Event) => {
    this.args.coachingConfig.schedulingWindowDays = parseInt(
      (event.target as HTMLInputElement).value,
      10
    );
  };

  setCoachConfirmationHoursBeforeStart = (event: Event) => {
    this.args.coachingConfig.coachConfirmationHoursBeforeStart = parseInt(
      (event.target as HTMLInputElement).value,
      10
    );
  };

  setCoachConfirmationDaysAfterBookingChemistry = (event: Event) => {
    const days = parseFloat((event.target as HTMLInputElement).value) || 0;
    this.args.coachingConfig.coachConfirmationHoursAfterBookingChemistry =
      daysToHours(days);
  };

  setCoachConfirmationDaysAfterBookingCoaching = (event: Event) => {
    const days = parseFloat((event.target as HTMLInputElement).value) || 0;
    this.args.coachingConfig.coachConfirmationHoursAfterBookingCoaching =
      daysToHours(days);
  };

  <template>
    <EditRecord
      @editLabel="Edit Coaching Configuration"
      @item={{@coachingConfig}}
      @context={{@context}}
      @canEdit={{@canEdit}}
      @maxDepth={{1}}
      @initialState={{initialState @coachingConfig}}
      @buttonSize="btn-md"
      ...attributes
    >
      <:form as |uniqueId isReadOnly|>
        <div class="card border shadow-sm rounded-3 mb-4">
          <div class="card-body p-4">
            <fieldset disabled={{isReadOnly}}>
              <legend class="gud-eyebrow">Template Details</legend>

              <div class="mb-0">
                <label
                  for="{{uniqueId}}-name"
                  class="form-label required"
                >Name</label>
                <input
                  type="text"
                  class="form-control"
                  id="{{uniqueId}}-name"
                  value={{@coachingConfig.name}}
                  placeholder="e.g. Standard 8h coaching, 60 min default"
                  required
                  {{debounce this.setName}}
                />
                <div class="form-text lh-sm">A descriptive name so this config
                  can be identified when assigning to cohorts.</div>
              </div>
            </fieldset>
          </div>
        </div>
        <div class="row mb-3">
          <div class="col-xl-6">

            <div class="card border shadow-sm rounded-3 mb-4">
              <div class="card-body p-4">
                <fieldset>
                  <legend
                    class="gud-eyebrow d-flex align-items-center gap-1"
                  >Chemistry sessions
                    <HelpPopover
                      @ariaLabel="About chemistry sessions"
                      @title="Chemistry sessions"
                      class="fs-4"
                    >
                      <p class="mb-0">Coachees book chemistry sessions with
                        coaches to help them decide if they want to work with
                        them. They are free and do not count towards the
                        coachee's coaching time allowance.</p>
                    </HelpPopover>
                  </legend>

                  <div class="row mb-0">
                    <div class="col-md-6">
                      <label
                        for="{{uniqueId}}-chemistryCallLimit"
                        class="form-label required"
                      >Chemistry call limit</label>
                      <div class="input-group">
                        {{#unless isReadOnly}}
                          <button
                            type="button"
                            class="btn btn-outline-primary rounded-start-1 py-1"
                            aria-label="Decrease chemistry call limit"
                            {{on "click" (fn this.adjustChemistryCallLimit -1)}}
                          >−</button>
                        {{/unless}}
                        <input
                          type="number"
                          class="form-control text-center fw-semibold bg-body-tertiary"
                          id="{{uniqueId}}-chemistryCallLimit"
                          value={{@coachingConfig.chemistryCallLimit}}
                          min="0"
                          required
                          disabled={{isReadOnly}}
                          {{on "change" this.setChemistryCallLimit}}
                        />
                        {{#unless isReadOnly}}
                          <button
                            type="button"
                            class="btn btn-outline-primary rounded-end-1 py-1"
                            aria-label="Increase chemistry call limit"
                            {{on "click" (fn this.adjustChemistryCallLimit 1)}}
                          >+</button>
                        {{/unless}}
                      </div>
                      <div class="form-text lh-sm">Maximum number of free
                        chemistry calls per coachee</div>
                    </div>

                    <div class="col-md-6">
                      <span
                        class="form-label required d-block"
                        id="{{uniqueId}}-chemistryLengthLabel"
                      >Chemistry session length</span>
                      <div
                        class="row g-2"
                        role="group"
                        aria-labelledby="{{uniqueId}}-chemistryLengthLabel"
                      >
                        {{#each this.chemistryLengthOptions as |mins|}}
                          {{#let
                            (this.isChemistrySessionLength mins)
                            as |isSelected|
                          }}
                            <div class="col-6">
                              <input
                                type="radio"
                                class="btn-check"
                                value={{mins}}
                                id="{{uniqueId}}-chemistryLength-{{mins}}"
                                checked={{isSelected}}
                                disabled={{isReadOnly}}
                                {{on
                                  "change"
                                  (fn this.setChemistrySessionLength mins)
                                }}
                              />
                              <label
                                for="{{uniqueId}}-chemistryLength-{{mins}}"
                                class="btn btn-sm rounded-2 w-100
                                  {{if
                                    isSelected
                                    'btn-primary'
                                    'btn-outline-primary'
                                  }}
                                  {{if
                                    isReadOnly
                                    (unless
                                      isSelected
                                      'border-white text-body-tertiary'
                                    )
                                  }}"
                              >{{formatCoachingTime mins}}</label>
                            </div>
                          {{/let}}
                        {{/each}}
                      </div>
                      <input
                        type="hidden"
                        id="{{uniqueId}}-chemistrySessionLengthMins"
                        value={{@coachingConfig.chemistrySessionLengthMins}}
                        required
                      />
                    </div>
                  </div>

                </fieldset>
                <hr class="my-4" />

                <fieldset>
                  <legend
                    class="gud-eyebrow d-flex align-items-center gap-1"
                  >Coaching sessions
                    <HelpPopover
                      @ariaLabel="About coaching sessions"
                      @title="Coaching sessions"
                      class="fs-4"
                    >
                      <p class="mb-0">Once a coachee has decided to work with a
                        coach, they can book a number of one-to-one coaching
                        sessions with them.</p>
                    </HelpPopover>
                  </legend>

                  <span
                    class="form-label required d-block"
                    id="{{uniqueId}}-coachingTimeLabel"
                  >Total one-to-one coaching time per coachee</span>
                  <div class="row">
                    <div class="col">
                      <div class="input-group">
                        {{#unless isReadOnly}}
                          <button
                            type="button"
                            class="btn btn-outline-primary rounded-start-1 py-1"
                            aria-label="Decrease coaching time by 15 minutes"
                            {{on
                              "click"
                              (fn this.adjustCoachingTimeMinutes -15)
                            }}
                          >−</button>
                        {{/unless}}
                        <div
                          class="form-control text-center fw-semibold bg-body-tertiary"
                          aria-labelledby="{{uniqueId}}-coachingTimeLabel"
                        >
                          {{formatCoachingTime
                            @coachingConfig.coachingTimeMinutes
                          }}
                        </div>
                        {{#unless isReadOnly}}
                          <button
                            type="button"
                            class="btn btn-outline-primary rounded-end-1 py-1"
                            aria-label="Increase coaching time by 15 minutes"
                            {{on
                              "click"
                              (fn this.adjustCoachingTimeMinutes 15)
                            }}
                          >+</button>
                        {{/unless}}
                      </div>
                    </div>
                    {{#unless isReadOnly}}
                      <div class="col">
                        <div class="row g-2">
                          <div class="col-6">
                            <label
                              for="{{uniqueId}}-coachingHours"
                              class="visually-hidden"
                            >Hours</label>
                            <div class="coaching-config-unit-input input-group">
                              <input
                                type="number"
                                class="form-control"
                                id="{{uniqueId}}-coachingHours"
                                value={{this.coachingHours}}
                                min="0"
                                {{on "change" this.setCoachingHours}}
                              />
                              <span
                                class="input-group-text coaching-config-unit-input-suffix"
                              >hours</span>
                            </div>
                          </div>
                          <div class="col-6">
                            <label
                              for="{{uniqueId}}-coachingMinutes"
                              class="visually-hidden"
                            >Minutes</label>
                            <div class="coaching-config-unit-input input-group">
                              <input
                                type="number"
                                class="form-control"
                                id="{{uniqueId}}-coachingMinutes"
                                value={{this.coachingMinutesPart}}
                                min="0"
                                max="59"
                                {{on "change" this.setCoachingMinutesPart}}
                              />
                              <span
                                class="input-group-text coaching-config-unit-input-suffix"
                              >min</span>
                            </div>
                          </div>
                        </div>
                        <input
                          type="hidden"
                          id="{{uniqueId}}-coachingTimeMinutes"
                          value={{@coachingConfig.coachingTimeMinutes}}
                          required
                        />
                      </div>
                    {{/unless}}
                  </div>

                  <span
                    class="form-label required d-block mt-3 mb-0"
                    id="{{uniqueId}}-allowedDurationsLabel"
                  >Allowed session durations</span>
                  <div class="form-text lh-sm mt-0 mb-2">Coachees can choose
                    from these durations when booking sessions.</div>
                  <div
                    class="row g-2 mb-4"
                    role="group"
                    aria-labelledby="{{uniqueId}}-allowedDurationsLabel"
                  >
                    {{#each this.allowedDurationOptions as |mins|}}
                      {{#let (this.isAllowedDuration mins) as |isAllowed|}}
                        <div class="col-4">
                          <input
                            type="checkbox"
                            class="btn-check"
                            value={{mins}}
                            id="{{uniqueId}}-allowedDuration-{{mins}}"
                            checked={{isAllowed}}
                            disabled={{isReadOnly}}
                            {{on "change" this.setAllowedDuration}}
                          />
                          <label
                            for="{{uniqueId}}-allowedDuration-{{mins}}"
                            class="btn btn-sm rounded-2 w-100
                              {{if
                                isAllowed
                                'btn-primary'
                                'btn-outline-primary'
                              }}
                              {{if
                                isReadOnly
                                (unless
                                  isAllowed 'border-white text-body-tertiary'
                                )
                              }}"
                          >{{formatCoachingTime mins}}</label>
                        </div>
                      {{/let}}
                    {{/each}}
                  </div>

                  <div class="row mt-1">
                    <div class="col-md-6">
                      <label
                        for="{{uniqueId}}-defaultSessionDuration"
                        class="form-label required"
                      >Default session duration</label>
                      <select
                        class="form-select"
                        id="{{uniqueId}}-defaultSessionDuration"
                        required
                        disabled={{isReadOnly}}
                        {{on "change" this.setDefaultDuration}}
                      >
                        {{#each this.sortedAllowedDurations as |mins|}}
                          <option
                            value={{mins}}
                            selected={{if
                              (eq
                                @coachingConfig.defaultSessionDurationMinutes
                                mins
                              )
                              true
                              false
                            }}
                          >{{formatCoachingTime mins}}</option>
                        {{/each}}
                      </select>
                    </div>
                    <div class="col-md-6">
                      <span
                        class="form-label d-block"
                        id="{{uniqueId}}-estimatedCoachingSessionsLabel"
                      >Estimated number of sessions</span>
                      <div
                        class="form-control bg-body-tertiary ps-3 overflow-hidden text-nowrap"
                        aria-labelledby="{{uniqueId}}-estimatedCoachingSessionsLabel"
                      >
                        {{#if this.canEstimateCoachingSessionCount}}
                          <strong><span
                              class="text-primary fw-bold"
                            >{{this.estimatedCoachingSessionCount}}</span>
                            &times;
                            {{formatCoachingTime
                              @coachingConfig.defaultSessionDurationMinutes
                            }}</strong>
                          {{#if this.hasEstimatedCoachingSessionRemainder}}
                            <span class="fw-normal text-body-secondary ms-1">
                              (+
                              {{formatCoachingTime
                                this.estimatedCoachingSessionRemainderMinutes
                              }}
                              unused)
                            </span>
                          {{/if}}
                        {{else}}
                          —
                        {{/if}}
                      </div>
                    </div>
                  </div>

                </fieldset>
              </div>
            </div>

          </div>
          <div class="col-xl-6">

            <div class="card border shadow-sm rounded-3 mb-4">
              <div class="card-body p-4">
                <fieldset disabled={{isReadOnly}}>
                  <legend class="gud-eyebrow">Coachee booking limits</legend>

                  <div class="row mb-0">
                    <div class="col-md-6">
                      <label
                        for="{{uniqueId}}-schedulingWindowDays"
                        class="form-label required"
                      >Scheduling window</label>
                      <div class="coaching-config-unit-input input-group">
                        <input
                          type="number"
                          class="form-control"
                          id="{{uniqueId}}-schedulingWindowDays"
                          value={{@coachingConfig.schedulingWindowDays}}
                          min="1"
                          required
                          {{on "change" this.setSchedulingWindowDays}}
                        />
                        <span
                          class="input-group-text coaching-config-unit-input-suffix"
                        >days</span>
                      </div>
                      <div class="form-text lh-sm">How far in advance sessions
                        can be booked</div>
                    </div>
                    <div class="col-md-6">
                      <label
                        for="{{uniqueId}}-cancellationWindowHours"
                        class="form-label required"
                      >Cancellation notice period</label>
                      <div class="coaching-config-unit-input input-group">
                        <input
                          type="number"
                          class="form-control"
                          id="{{uniqueId}}-cancellationWindowHours"
                          value={{@coachingConfig.cancellationWindowHours}}
                          min="0"
                          required
                          {{on "change" this.setCancellationWindowHours}}
                        />
                        <span
                          class="input-group-text coaching-config-unit-input-suffix"
                        >hours</span>
                      </div>
                      <div class="form-text lh-sm">Notice required for a free
                        cancellation. Late cancellations are chargeable.</div>
                    </div>
                  </div>
                </fieldset>

                <hr class="my-4" />

                <fieldset>
                  <legend
                    class="gud-eyebrow d-flex align-items-center gap-1"
                  >Coach acceptance
                    <HelpPopover
                      @ariaLabel="About coach acceptance"
                      @title="Coach acceptance"
                      class="fs-4"
                    >
                      <p>When a coachee books a session, the coach must accept
                        or decline it in Xceed. Accepting confirms they are
                        available and lets the coachee know the session will go
                        ahead. Declining removes the booking and refunds the
                        coachee.</p>
                      <p>If the coach does not respond in time, the booking is
                        canceled automatically and the coachee is refunded.</p>
                      <p><strong>How the deadline is calculated.</strong>
                        Both settings below apply. The coach must respond by
                        <em>whichever comes first</em>:</p>
                      <ul>
                        <li><strong>Booking response window</strong>
                          — days after the coachee submits the booking</li>
                        <li><strong>Response notice period</strong>
                          — hours before the session starts</li>
                      </ul>
                      <p>For a session booked far in advance, the booking
                        response window usually applies. For a session booked
                        closer to its start time, the response notice period
                        usually applies.</p>
                      <p>For coaching sessions, set the booking response window
                        to
                        <strong>0</strong>
                        to use only the response notice period.</p>
                    </HelpPopover>
                  </legend>

                  <p class="mb-3">Booking response window</p>

                  <div class="row mb-4">
                    <div class="col-md-6">
                      <label
                        for="{{uniqueId}}-coachConfirmationDaysAfterBookingChemistry"
                        class="form-label required"
                      >Chemistry calls</label>
                      <div class="coaching-config-unit-input input-group">
                        <input
                          type="number"
                          class="form-control"
                          id="{{uniqueId}}-coachConfirmationDaysAfterBookingChemistry"
                          value={{this.coachConfirmationDaysAfterBookingChemistry}}
                          min="1"
                          step="1"
                          required
                          disabled={{isReadOnly}}
                          {{on
                            "change"
                            this.setCoachConfirmationDaysAfterBookingChemistry
                          }}
                        />
                        <span
                          class="input-group-text coaching-config-unit-input-suffix"
                        >days after booking</span>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <label
                        for="{{uniqueId}}-coachConfirmationDaysAfterBookingCoaching"
                        class="form-label required"
                      >Coaching sessions</label>
                      <div class="coaching-config-unit-input input-group">
                        <input
                          type="number"
                          class="form-control"
                          id="{{uniqueId}}-coachConfirmationDaysAfterBookingCoaching"
                          value={{this.coachConfirmationDaysAfterBookingCoaching}}
                          min="0"
                          step="1"
                          required
                          disabled={{isReadOnly}}
                          {{on
                            "change"
                            this.setCoachConfirmationDaysAfterBookingCoaching
                          }}
                        />
                        <span
                          class="input-group-text coaching-config-unit-input-suffix"
                        >days after booking</span>
                      </div>
                      <div class="form-text lh-sm">Enter 0 for no response
                        window (coach can respond at any time up to the response
                        notice period below).</div>
                    </div>
                  </div>

                  <div>
                    <label
                      for="{{uniqueId}}-coachConfirmationHoursBeforeStart"
                      class="form-label required"
                    >Response notice period</label>
                    <div class="form-text lh-sm mb-2">Hours
                      <em>before the session starts</em>
                      by which the coach must respond. Used together with the
                      booking response window above; whichever deadline comes
                      first applies.</div>
                    <div class="col-md-6">
                      <div class="coaching-config-unit-input input-group">
                        <input
                          type="number"
                          class="form-control"
                          id="{{uniqueId}}-coachConfirmationHoursBeforeStart"
                          value={{@coachingConfig.coachConfirmationHoursBeforeStart}}
                          min="1"
                          disabled={{isReadOnly}}
                          required
                          {{on
                            "change"
                            this.setCoachConfirmationHoursBeforeStart
                          }}
                        />
                        <span
                          class="input-group-text coaching-config-unit-input-suffix"
                        >hours</span>
                      </div>
                    </div>
                    <div
                      class="alert alert-warning mt-3 mb-0 py-2 lh-sm"
                      role="alert"
                    >
                      <p class="mb-0">Coachees cannot book sessions starting
                        within
                        {{@coachingConfig.coachConfirmationHoursBeforeStart}}
                        hours. The coach needs at least that much time before
                        start to accept or decline.</p>
                    </div>
                  </div>

                </fieldset>
              </div>
            </div>
          </div>
        </div>
      </:form>
    </EditRecord>
  </template>
}
